/**
 * `<MapEditor/>` — the Tasks-team Map workspace (the #759 hero surface).
 *
 * Issue #759, Phase 2. The clean orchestrating shell: it loads the team's
 * nodes (`useTaskNodes`), nests them (`buildTree`), resolves members for
 * avatars, holds the shared UI state (`treeStore`: view mode + selection +
 * inline-edit cursor + collapse), and wires every editor gesture to exactly
 * one mutation. It owns no rendering detail (those live in the components)
 * and no tree maths (that lives in the pure modules). The split (§A7): data
 * hooks ⟂ pure tree/outline modules ⟂ views, joined here.
 *
 * Screens shipped through this component:
 *   - B1.3 empty workspace (zero nodes → name-the-outcome prompt).
 *   - B2.1 map home / tree canvas (PR-1) + drag-to-reparent (PR-2).
 *   - B2.2 node focused (focus + inline quick-actions, via the card).
 *   - B2.3 outline editor (full-screen keyboard outline — PR-2).
 *   - B2.4 split view (outline ⇄ tree, live two-way sync — PR-2).
 *   - B8.3 large tree collapsed (collapse/expand chips, via the card).
 *
 * The three editor surfaces share one structure: the tree canvas, the
 * outline editor, and the split (which composes both). The same data +
 * selection + mutations flow into whichever surface is showing, so editing
 * on either side of the split updates the other instantly (two-way sync).
 *
 * Role fork (#761, §A8): the FULL editor below is the LEAD experience. A
 * non-lead member gets the member-oriented experience (`<MemberMapView/>`:
 * My-work + a read-highlighted full map). The decision is made ONCE here —
 * `useCurrentMember().isLead` — so the role-branching is centralised, not
 * scattered. The whole tree is visible to both (§A8); the difference is the
 * surface, not the data scope.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { TeamZoneContext } from "./theme/TeamZoneContext";

import { ApiError } from "../api/client";
import { useTeam } from "../api/useTeam";
import { useTeamFeature } from "../api/useTeamFeature";
import { useBreadcrumbs } from "../components/Breadcrumbs";
import { useIsMobile } from "../lib/useMediaQuery";
import { MemberMapView } from "./MemberMapView";
import {
  useArchiveTaskNode,
  useAssignTaskMember,
  useCreateLinkBlock,
  useCreateTaskNode,
  usePatchTaskNode,
  useRestoreTaskNode,
  useSetNodePosition,
  useTaskNodes,
  useUnassignTaskMember,
} from "./api/taskNodes";
import { useAddNodeNote } from "./api/useNodeNotes";
import {
  type TaskNotification,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "./api/useNotifications";
import { useTeamReminders } from "./api/useReminders";
import {
  useAddTaskDependency,
  useInsertNodeBetween,
  useRemoveTaskDependency,
  useSetDependencyNote,
  useTaskDependencies,
} from "./api/useTaskDependencies";
import { useCreateTaskSnapshot } from "./api/useTaskSnapshots";
import { CompareView } from "./compare/CompareView";
import { CompletedPlanBanner } from "./components/CompletedPlanBanner";
import { EmptyWorkspace } from "./components/EmptyWorkspace";
import { MapSearch } from "./components/MapSearch";
import { MobileMapView } from "./components/MobileMapView";
import { NodeDetailDrawer } from "./components/NodeDetailDrawer";
import { OutlineEditor } from "./components/OutlineEditor";
import { RemindersBell } from "./components/RemindersBell";
import { RunSheetPanel } from "./components/RunSheetPanel";
import { SplitView } from "./components/SplitView";
import { TaskCanvas } from "./components/TaskCanvas";
import { TaskOutlineRail } from "./components/TaskOutlineRail";
import { TemplatePicker } from "./components/TemplatePicker";
import { type TimeMachineEntry, TimeMachineMenu } from "./components/TimeMachineMenu";
import { ViewModeToggle } from "./components/ViewModeToggle";
import { badgeLabel } from "./components/notificationText";
import { MapChangeDigest } from "./digest/MapChangeDigest";
import { NextStepCoach } from "./guidance/NextStepCoach";
import { useCurrentMember } from "./members/useCurrentMember";
import { useTaskTeamMembers } from "./members/useTaskTeamMembers";
import type { CreateTarget, MoveTarget } from "./outline/outlineModel";
import { wouldCreateCycle } from "./outline/outlineModel";
import { RewindView } from "./rewind/RewindView";
import { NotificationPrefsMatrix } from "./settings/NotificationPrefsMatrix";
import { SnapshotsView } from "./snapshots/SnapshotsView";
import { useMapTheme } from "./theme/MapThemeContext";
import { buildLinkBlocks, buildTree, flattenTree } from "./tree/buildTree";
import { wouldCreateDependencyCycle } from "./tree/dependencyGraph";
import { isBoardComplete, totalLeafCount } from "./tree/outcomes";
import { PALETTE } from "./tree/palette";
import type { TaskState } from "./tree/state";
import { useTreeUiStore } from "./tree/treeStore";

/**
 * The Map workspace entry point. Branches by role: leads get the full
 * two-way editor (`<LeadMapEditor/>`); members get `<MemberMapView/>`. We
 * gate on `me.loading` so we never flash the wrong surface during the brief
 * role-resolution window (the auth + team-role fetches).
 */
export function MapEditor() {
  const { teamId } = useParams<{ teamId: string }>();
  const me = useCurrentMember(teamId);

  if (me.loading) {
    return (
      <div
        data-testid="map-role-loading"
        className="flex h-[calc(100vh-8rem)] items-center justify-center"
        style={{ backgroundColor: PALETTE.canvas, color: PALETTE.muted }}
      >
        Loading the map…
      </div>
    );
  }

  return me.isLead ? <LeadMapEditor /> : <MemberMapView />;
}

/** The lead's full two-way editor (the #759/#760 surface). */
function LeadMapEditor() {
  const { teamId } = useParams<{ teamId: string }>();
  const teamQuery = useTeam(teamId);
  const teamName = teamQuery.data?.name ?? "This team";

  // B7.3 breadcrumb (team Home + Taskmap). PR-D (#1043): the editor lives at
  // `/map/board`; the team-name segment links to Home (the bare `/map`), the
  // current segment is "Taskmap".
  useBreadcrumbs(
    teamId
      ? [{ label: teamName, to: `/teams/${teamId}/map` }, { label: "Taskmap" }]
      : [{ label: "Taskmap" }],
  );

  const nodesQuery = useTaskNodes(teamId);
  const { list: members } = useTaskTeamMembers(teamId);

  const [runSheetOpen, setRunSheetOpen] = useState(false);
  const createNode = useCreateTaskNode(teamId);
  const patchNode = usePatchTaskNode(teamId);
  const archiveNode = useArchiveTaskNode(teamId);
  // Round-4 ⑤.d (#1043) — the broken-link cue on the canvas calls this when a
  // dep partner is archived, so a one-click Restore brings the link back. No
  // success callback wired beyond the hook's own cache reconcile (the live
  // tree refetch re-mounts the edge; the cue dismounts naturally).
  const restoreNode = useRestoreTaskNode(teamId);
  // Round-4 ⑥ (#1043) — pin (or reset) a card's canvas position; create a
  // freeform `link_block` annotation. The canvas wires its drag-to-pin drop +
  // overflow "Reset to auto layout" to the pin mutation; the "+ link block"
  // affordance fires the create mutation.
  const setNodePosition = useSetNodePosition(teamId);
  const createLinkBlock = useCreateLinkBlock(teamId);
  const assignMember = useAssignTaskMember(teamId);
  const unassignMember = useUnassignTaskMember(teamId);

  const {
    viewMode,
    selectedId,
    selectedEdgeId,
    editingNodeId,
    openNodeId,
    openTab,
    focusNoteId,
    collapsed,
    linkArmId,
    setViewMode,
    select,
    selectEdge,
    setEditing,
    openNode,
    clearFocusNote,
    setLinkArm,
    toggleCollapse,
    expand,
    reset,
  } = useTreeUiStore();

  // A calm, transient banner for a rejected drag-reparent (server 422
  // `TaskCycleError`) — factual, not alarm copy (§A9).
  const [reparentNotice, setReparentNotice] = useState<string | null>(null);

  // The worked-example template picker (#985), reachable from the empty
  // workspace's secondary "Start from an example →" entry. Lead-only (this is
  // the lead editor; adopting is server-enforced lead-only). Adopting clones a
  // template into new roots + invalidates the task-nodes query, so the canvas
  // re-renders populated and this branch's empty state falls away.
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  // The dependency layer (#766), gated behind the `task_dependencies` component
  // key (off by default, §A10). When OFF: no arrows on the canvas, no link
  // handle, no drawer rows — the editor is byte-unchanged. When ON: the layer
  // appears. The fetch + mutations only fire / matter behind this gate; the
  // backend enforces it too (404 when off). A separate transient notice for a
  // rejected link keeps it distinct from the reparent one.
  const dependenciesEnabled = useTeamFeature(teamId, "task_dependencies") === true;
  const dependenciesQuery = useTaskDependencies(dependenciesEnabled ? teamId : undefined);
  const addDependency = useAddTaskDependency(teamId);
  const removeDependency = useRemoveTaskDependency(teamId);
  // #929 — the edge popover's two new mutations: set/clear the free-text note
  // (PATCH) and split the edge by inserting a node between (POST). Both share
  // the same gate as the layer; the insert hook invalidates the task-nodes
  // query too so the new node + rewired edges appear without a reload.
  const setDependencyNote = useSetDependencyNote(teamId);
  const insertNodeBetween = useInsertNodeBetween(teamId);
  const [dependencyNotice, setDependencyNotice] = useState<string | null>(null);

  // B3.3 rewind (#764), gated behind the `task_rewind` component key (off by
  // default). The entry point + the surface only appear when the gate is on;
  // when off the live editor is completely unchanged (no broken affordance).
  const rewindEnabled = useTeamFeature(teamId, "task_rewind") === true;
  const [rewindOpen, setRewindOpen] = useState(false);

  // M1 (#1043, Wave 6) — the editorial Map's phone layout. We render the
  // outline-first `<MobileMapView/>` ONLY when on a phone AND the editorial
  // theme is on; the desktop / legacy body is byte-unchanged (the gate). The
  // canvas is still reachable on a phone via the action bar's "⊞ canvas"
  // toggle, which drops to the existing tree surface for that one session.
  const theme = useMapTheme();
  const editorial = theme.mode === "editorial";
  const isMobile = useIsMobile();
  const mobileEditorial = isMobile && editorial;
  const [mobileShowCanvas, setMobileShowCanvas] = useState(false);

  // Round-3 (#1043, founder prod-inspection items 2 + 3, 2026-05-27) — the
  // docked History right-rail (PR-C) is REMOVED from the Taskmap window. Project
  // activity now lives on the Home dashboard as a project-wide feed beside the
  // task list (`<HomeDashboard/>`). The Taskmap is the canvas/outline alone
  // again, so it reclaims the full width. Per-node history still lives in the
  // node drawer's History tab (unchanged).

  // B3.4 / B3.5 deeper time machine (#765), each behind its own component key
  // (off by default). Same additive pattern as rewind: the toolbar entry +
  // the surface appear only when the gate is on; the live editor is untouched
  // when off. The digest (B3.6) is its own gated panel below (`task_change_digest`).
  const snapshotsEnabled = useTeamFeature(teamId, "task_snapshots") === true;
  const compareEnabled = useTeamFeature(teamId, "task_compare") === true;
  const [timeMachine, setTimeMachine] = useState<"snapshots" | "compare" | null>(null);

  // B4.1 notes (#767), gated behind `task_notes` (off by default, §A10). The
  // drawer's Notes tab fetches its own thread (lazily, on tab-open) when this
  // is on; when off the tab shows the calm placeholder. The live editor is
  // byte-unchanged either way — notes live entirely inside the drawer.
  const notesEnabled = useTeamFeature(teamId, "task_notes") === true;

  // #953 — a Blocked reason typed in the change-state menu ON THE CANVAS CARD
  // persists as a note, exactly as the drawer already does (the drawer wires
  // `onSaveReason` → `addNote.mutate({ body })`). The card's change-state menu
  // only ever mounts on the FOCUSED node (B2.2 quick-actions), so we key the
  // add-note mutation to `selectedId` — the very node being blocked. When the
  // notes layer is off, no handler is passed, so the reason step degrades to
  // its calm placeholder (commit Blocked, no note) — unchanged from #760.
  const addCardNote = useAddNodeNote(teamId, selectedId ?? undefined);
  const saveCardBlockedReason =
    notesEnabled && selectedId
      ? (reason: string) => {
          addCardNote.mutate({ body: reason });
        }
      : undefined;

  // B4.2 reminders (#768), gated behind `task_reminders` (off by default,
  // §A10). When ON: a 🔔 toolbar entry opens the team reminder feed (the bell),
  // and the drawer gains a Reminders tab to SET a forward nudge on a node. When
  // OFF: no bell, no drawer tab — the editor is byte-unchanged. Both surfaces
  // fetch lazily (only when shown); the backend enforces the gate too.
  const remindersEnabled = useTeamFeature(teamId, "task_reminders") === true;

  // B2.12 estimates (#1043), gated behind `task_estimates` (off by default,
  // §A10). When ON: the drawer's "Estimate" row becomes a coarse-bucket picker
  // and a parent shows the read-time sum-of-leaves roll-up; the node card shows
  // a node's set estimate next to its state chip. When OFF: the row stays
  // display-only, no card meta — the editor is byte-unchanged. Estimates are
  // coarse + display-only, never a contract or a per-person commitment (§A9).
  const estimatesEnabled = useTeamFeature(teamId, "task_estimates") === true;
  // B4.4 (#1043), gated behind `task_notification_prefs` (off by default, §A10).
  // When ON, the bell footer's "notification settings" link OPENS the per-event x
  // channel matrix (a dialog); when OFF, the footer keeps the honest #907 popover.
  const notificationPrefsEnabled = useTeamFeature(teamId, "task_notification_prefs") === true;
  const [notificationPrefsOpen, setNotificationPrefsOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  // The team reminder feed (the bell). Fetches only while the bell is open AND
  // the layer is on; degrades calmly on a 404 (undeployed / ungated).
  const teamReminders = useTeamReminders(teamId, bellOpen && remindersEnabled);

  // #907 — note notifications fold into the SAME 🔔 bell (one attention
  // surface). Gated under `task_notes` (same key as the Notes layer). The bell
  // mounts when EITHER reminders OR notes is on. Two read shapes off one query:
  //   - a cheap unread-count fetch drives the toolbar badge (runs whenever the
  //     notifications layer is on, so the badge is live without opening the bell);
  //   - the full feed loads when the bell is open.
  const notificationsEnabled = notesEnabled;
  const notificationBadge = useNotifications(teamId, notificationsEnabled, { full: false });
  const notificationFeed = useNotifications(teamId, bellOpen && notificationsEnabled, {
    full: true,
  });
  const markRead = useMarkNotificationRead(teamId);
  const markAllRead = useMarkAllNotificationsRead(teamId);
  // The bell exists when either attention layer is on.
  const bellMounted = remindersEnabled || notificationsEnabled;

  // The signed-in member's id on this team — scopes the B3.6 digest's "last
  // looked" stamp AND the notes delete affordance (your own notes). A lead is
  // also a member; the digest is THEIR own slice.
  const me = useCurrentMember(teamId);

  // Reset transient UI state when the team changes (one global store).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on team switch only
  useEffect(() => {
    reset();
  }, [teamId, reset]);

  // #1043 (IA shell, founder 2026-05-26) — the editorial Taskmap is ONE window
  // with the outline shown by default + collapsible. We model that with the
  // shared `viewMode` constrained to two values in editorial:
  //   - "split"  = outline rail + canvas (the DEFAULT — outline SHOWN);
  //   - "tree"   = canvas-only (outline HIDDEN, via the collapse control).
  // `reset()` (above) slams `viewMode` back to "tree" on mount/team-change, so
  // this effect — declared AFTER it on purpose, since both fire on mount — has
  // the last word and lands the editorial default on "split" (outline shown).
  // It only opens the split on a FRESH editorial mount (when `viewMode` is the
  // reset's "tree"); once the user collapses to "tree" themselves the effect
  // won't re-fire (no dep changed), so a deliberate collapse is respected.
  // EDITORIAL-ONLY: with the flag off this is a no-op, so the legacy editor's
  // view-mode behaviour (the corner `<ViewModeToggle/>`) is byte-unchanged.
  // biome-ignore lint/correctness/useExhaustiveDependencies: open the editorial default once per team mount
  useEffect(() => {
    if (editorial) setViewMode("split");
  }, [editorial, teamId, setViewMode]);

  // Guard against a non-array body (errored/unexpected response) so the
  // tree build never throws — degrade to "no nodes" (the empty workspace).
  const flat = Array.isArray(nodesQuery.data) ? nodesQuery.data : [];
  const roots = buildTree(flat);
  // Round-4 ⑥ (#1043) — off-tree `link_block` nodes built separately so the
  // canvas can render them as freeform annotations alongside the tidy-tree.
  // `buildTree` already filters them OUT of the tree shape; here we collect
  // the leaf list with their pinned coordinates.
  const linkBlocks = buildLinkBlocks(flat);
  const isEmpty = !nodesQuery.isLoading && flat.length === 0;

  // node id → title, so the drawer's History tab (#763) can name referenced
  // nodes ("created under '…'", "comes after '…'") from the one live source.
  const nodeTitleById = new Map(flat.map((n) => [n.id, n.title]));

  // Round-4 ⑤.d + Bug A (#1043) — the canvas's broken-link cue needs to tell a
  // missing dep-endpoint apart: ARCHIVED (not in the live tree, partner removed)
  // vs COLLAPSED (in the live tree, hidden by a folded ancestor). The live id
  // set is every id in `flat` (which `useTaskNodes` filters to live = archived
  // excluded). The forest adjacency lets the cue walk a hidden endpoint's
  // parent chain up to the collapsed ancestor so one click can un-collapse it.
  const liveNodeIds = new Set(flat.map((n) => n.id));
  const liveForestById = new Map(
    flat.map((n) => [n.id, { id: n.id, parentId: n.parent_id ?? null }]),
  );

  // B8.4 (#1043) — the DERIVED completed-plan treatment. The WHOLE board is
  // complete when there's ≥1 task AND every outcome's leaves are `done`
  // (`isBoardComplete`, pure). When complete, the top "PLAN COMPLETED" banner
  // shows + the canvas's per-outcome badges/lanes flip to done-green (the canvas
  // derives those itself from the same rollup). Re-opening any node off `done`
  // un-completes the board and clears the treatment — no stored archive flag.
  // EDITORIAL-only: the banner is editorial chrome, so legacy never shows it.
  const boardComplete = editorial && isBoardComplete(roots);
  const leafCount = totalLeafCount(roots);

  // The `snapshot & close` action on the completed banner (#1043, B8.4) reuses
  // the EXISTING snapshot-pin mutation — it pins a closing snapshot "now" and
  // does NOT archive/delete any node (the done tree stays read-only-visible). It
  // is offered ONLY when the snapshots layer is on; when off, the banner shows
  // with no snapshot action (the completion is still surfaced). Labelled with
  // the team + day so the closing bookmark is self-describing.
  const createSnapshot = useCreateTaskSnapshot(teamId);
  const pinClosingSnapshot = () => {
    createSnapshot.mutate({ label: "Plan completed" });
  };

  // ── Mutation wiring ──────────────────────────────────────────────────
  const createRoot = (title: string) => createNode.mutate({ title, parentId: null });

  /**
   * Create a node at an outline target. When `focusEmpty` is set (the
   * keyboard Enter / + outcome flow) the new, empty-titled row opens in edit
   * mode so the user keeps typing — the new id comes back in the mutation
   * response, so we can focus it without a second round-trip.
   */
  const createAt = (target: CreateTarget, opts?: { focusEmpty?: boolean }) => {
    createNode.mutate(
      {
        title: opts?.focusEmpty ? "" : "New task",
        parentId: target.parentId,
        orderIndex: target.orderIndex,
      },
      {
        onSuccess: (res) => {
          if (target.parentId) expand(target.parentId);
          if (opts?.focusEmpty && res?.node?.id) setEditing(res.node.id);
        },
      },
    );
  };

  const addSubtask = (parentId: string) => {
    const parent = flattenTree(roots).find((n) => n.id === parentId);
    const orderIndex = parent ? parent.children.length : 0;
    createNode.mutate(
      { title: "New task", parentId, orderIndex },
      { onSuccess: () => expand(parentId) },
    );
  };

  // The toolbar "+ Add": a sub-task under the focused node, else a root.
  const addFromToolbar = () => {
    if (selectedId) addSubtask(selectedId);
    else createRoot("New outcome");
  };

  // The editorial "+ outcome" affordance (#1043, B2.14). ALWAYS creates a new
  // ROOT (a new outcome) regardless of the current focus — distinct from "+ Add"
  // (which adds a sub-task under a focused node). The new root opens empty in
  // inline-edit so the user names it straight away (the same `focusEmpty` →
  // `setEditing(res.node.id)` flow the keyboard add uses), and is selected so
  // the rename input belongs to it.
  const createOutcome = () => {
    createNode.mutate(
      { title: "", parentId: null },
      {
        onSuccess: (res) => {
          if (res?.node?.id) {
            select(res.node.id);
            setEditing(res.node.id);
          }
        },
      },
    );
  };

  const setState = (id: string, state: TaskState) =>
    patchNode.mutate({ nodeId: id, patch: { state } });
  const rename = (id: string, title: string) => patchNode.mutate({ nodeId: id, patch: { title } });
  const setDescription = (id: string, description: string) =>
    patchNode.mutate({ nodeId: id, patch: { description: description.trim() || null } });
  // B2.11 (#1043) — set/clear a node's optional target date. `date` is an ISO
  // `YYYY-MM-DD` string, or null to clear. One thin `patchNode` call → one
  // server-side `target_date_set` event (mirrors `setState`/`setDescription`).
  const setTargetDate = (id: string, date: string | null) =>
    patchNode.mutate({ nodeId: id, patch: { targetDate: date } });
  // B2.12 (#1043) — set/clear a node's coarse estimate. `minutes` is the bucket
  // value (or a custom days→minutes), null to clear. One thin `patchNode` call →
  // one server-side `estimate_set` event (mirrors `setTargetDate`).
  const setEstimate = (id: string, minutes: number | null) =>
    patchNode.mutate({ nodeId: id, patch: { estimateMinutes: minutes } });
  const move = (id: string, target: MoveTarget) =>
    patchNode.mutate({
      nodeId: id,
      patch: { parentId: target.parentId, orderIndex: target.orderIndex },
    });
  const reorder = (id: string, orderIndex: number) =>
    patchNode.mutate({ nodeId: id, patch: { orderIndex } });

  // The focused-card ◀/▶ "arrange for viewing" controls (#961) were removed in
  // round-4 item ② (#1043) so this editor no longer wires a `reorderSibling`
  // handler. The pure helpers (`siblingSwap`/`canReorderSibling` in
  // `outline/outlineModel.ts`) remain available for a future outline-side
  // reorder gesture.

  // Remove a task from the map by SOFT-archiving it (recoverable; history kept).
  // `cascade` selects single-node vs whole-branch removal (#1043): a leaf (or a
  // card-level remove) archives the one node; the drawer's parent affordance
  // passes `cascade:true` so a whole branch comes off in one action instead of
  // re-rooting its children (resolving the #1087 follow-up).
  const archive = (id: string, cascade = false) => {
    archiveNode.mutate({ nodeId: id, cascade });
    if (selectedId === id) select(null);
    if (editingNodeId === id) setEditing(null);
    if (openNodeId === id) openNode(null);
  };
  const assignToggle = (nodeId: string, membershipId: string, assign: boolean) => {
    if (assign) assignMember.mutate({ nodeId, membershipId });
    else unassignMember.mutate({ nodeId, membershipId });
  };

  /**
   * Re-parent a task on the canvas. Round-4 ⑥ (#1043) — fired by the card's
   * "Move under…" overflow item (the old drag-a-card-onto-another gesture is
   * gone; plain card drag is now drag-to-pin). `newParentId === null`
   * promotes the task to a root outcome. Rejects a cycle-creating pick up
   * front (snappy), and falls back to a calm notice on a server 422.
   */
  const reparent = (childId: string, newParentId: string | null) => {
    const child = flattenTree(roots).find((n) => n.id === childId);
    if (newParentId !== null && child && wouldCreateCycle(child, newParentId)) {
      setReparentNotice("That move would put a task inside itself — not changed.");
      return;
    }
    const parent =
      newParentId !== null ? flattenTree(roots).find((n) => n.id === newParentId) : null;
    const orderIndex = parent ? parent.children.length : 0;
    patchNode.mutate(
      { nodeId: childId, patch: { parentId: newParentId, orderIndex } },
      {
        onSuccess: () => {
          if (newParentId !== null) expand(newParentId);
          setReparentNotice(null);
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 422) {
            setReparentNotice("That move would put a task inside itself — not changed.");
          }
        },
      },
    );
  };

  // ── Dependency wiring (#766) ─────────────────────────────────────────
  //
  // Draw a `from ──▶ to` edge ("from comes before to"). Reject a
  // cycle-creating link up front (snappy, over the current edge set) and
  // handle the server's 422 `TaskCycleError` + 403 (not author) gracefully —
  // a calm, factual notice, never a crash or alarm copy (§A9).
  const linkDependency = (fromNodeId: string, toNodeId: string) => {
    if (wouldCreateDependencyCycle(dependenciesQuery.dependencies, fromNodeId, toNodeId)) {
      setDependencyNotice("That link would loop the order back on itself — not added.");
      return;
    }
    addDependency.mutate(
      { fromNodeId, toNodeId },
      {
        onSuccess: () => setDependencyNotice(null),
        onError: (err) => {
          if (err instanceof ApiError && err.status === 422) {
            setDependencyNotice("That link would loop the order back on itself — not added.");
          } else if (err instanceof ApiError && err.status === 403) {
            setDependencyNotice("Only someone working on the later task can set what it waits on.");
          }
        },
      },
    );
  };
  const unlinkDependency = (dependencyId: string) => removeDependency.mutate(dependencyId);

  // ── Edge popover wiring (#929) ───────────────────────────────────────
  //
  // The canvas selects an edge (`selectEdge`); the popover then sets/clears its
  // note, inserts a node between its endpoints, or removes it. Each fires the
  // matching gated hook. Removing the SELECTED edge clears the selection (the
  // edge — and its popover — is gone). Inserting closes the popover too: the
  // original edge no longer exists (it became from→new→to), so a stale
  // selection would dangle.
  const setEdgeNote = (dependencyId: string, note: string | null) =>
    setDependencyNote.mutate({ dependencyId, note });
  const removeEdge = (dependencyId: string) => {
    removeDependency.mutate(dependencyId);
    if (selectedEdgeId === dependencyId) selectEdge(null);
  };
  const insertNodeBetweenEdge = (dependencyId: string, title: string) =>
    insertNodeBetween.mutate({ dependencyId, title }, { onSuccess: () => selectEdge(null) });
  const edgeMutationPending =
    setDependencyNote.isPending || insertNodeBetween.isPending || removeDependency.isPending;

  // ── Notification deep-link (#907) ────────────────────────────────────
  //
  // Clicking a notification "brings them to the exact place": open the node's
  // drawer ON the Notes tab, with the referenced note id passed so the tab
  // scrolls to + briefly highlights it. The same click marks the notification
  // read (its own slice; idempotent) and closes the bell.
  const openNotification = (notification: TaskNotification) => {
    openNode(notification.node_id, { tab: "notes", noteId: notification.note_id });
    markRead.mutate(notification.id);
    setBellOpen(false);
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (nodesQuery.isLoading) {
    return (
      <div
        data-testid="map-editor-loading"
        className="flex h-[calc(100vh-8rem)] items-center justify-center"
        style={{ backgroundColor: PALETTE.canvas, color: PALETTE.muted }}
      >
        Loading the map…
      </div>
    );
  }

  // B3.3 rewind surface (#764). Opens over the editor when the gate is on AND
  // the user entered it; "Today / Live" returns here. Rendered before the
  // empty-state check so it's reachable independent of the live tree's state
  // (a past day may differ from "now"). The rewind owns its own data + canvas.
  if (rewindOpen && rewindEnabled) {
    return <RewindView teamId={teamId} members={members} onExit={() => setRewindOpen(false)} />;
  }

  // B3.4 snapshots / B3.5 compare surfaces (#765). Like the rewind, they open
  // OVER the editor and own their own data + canvas; "← Map" returns here.
  // Rendered before the empty-state check so they're reachable independent of
  // the live tree's state (a snapshot/compare may show a tree the live one no
  // longer does).
  if (timeMachine === "snapshots" && snapshotsEnabled) {
    return <SnapshotsView teamId={teamId} members={members} onExit={() => setTimeMachine(null)} />;
  }
  if (timeMachine === "compare" && compareEnabled) {
    return <CompareView teamId={teamId} members={members} onExit={() => setTimeMachine(null)} />;
  }

  if (isEmpty) {
    return (
      <div data-testid="map-editor" className="h-[calc(100vh-8rem)]">
        <EmptyWorkspace
          onCreateRoot={createRoot}
          pending={createNode.isPending}
          // The secondary "Start from an example →" entry (#985). Passing this
          // from the LEAD editor is what gates it lead-only — a non-lead gets
          // `<MemberMapView/>` and never reaches here.
          onStartFromExample={() => setTemplatePickerOpen(true)}
        />
        {/* The worked-example picker floats over the dimmed empty workspace.
            Adopting clones the template into new roots + invalidates the
            task-nodes query, so this branch re-renders into the live tree (with
            the #980 next-step coach guiding the lead's next move); the picker's
            onClose fires on success too. */}
        {templatePickerOpen && (
          <TemplatePicker teamId={teamId} onClose={() => setTemplatePickerOpen(false)} />
        )}
      </div>
    );
  }

  // Props shared verbatim by the full-screen outline AND the split's left
  // pane — one outline implementation, never forked.
  const outlineProps = {
    roots,
    members,
    selectedId,
    editingNodeId,
    // #960 — single-click a row opens the node's detail drawer (also selects).
    onOpenNode: openNode,
    onSetEditing: setEditing,
    onRename: rename,
    onCreate: createAt,
    onMove: move,
    onReorder: reorder,
    onAssignToggle: assignToggle,
    onRemove: archive,
    // #1043 — the "/" command menu changes a row's state (same `patchNode`
    // path the card + drawer use) and, when the dependency layer is on, arms
    // click-to-link FROM the row (the canvas then takes the target click,
    // #904 — reuse, no duplicated dependency logic). Collapse reuses the SAME
    // shared `treeStore.collapsed` set the canvas reads (B8.3), so folding a
    // subtree in the outline folds it on the canvas too.
    onSetState: setState,
    ...(dependenciesEnabled ? { onArmDependency: setLinkArm } : {}),
    collapsed,
    onToggleCollapse: toggleCollapse,
  };

  // Props shared verbatim by the tree home AND the split's right pane. The
  // dependency layer (arrows + drag-to-link) is added ONLY when the gate is on;
  // when off these props are `undefined` and the canvas renders byte-unchanged.
  const canvasProps = {
    roots,
    members,
    collapsed,
    selectedId,
    onSelect: select,
    onToggleCollapse: toggleCollapse,
    onSetState: setState,
    onAddSubtask: addSubtask,
    onRename: rename,
    onArchive: archive,
    onAssignToggle: assignToggle,
    onReparent: reparent,
    onOpenDrawer: openNode,
    // Round-4 ⑤.d + Bug A (#1043) — the broken-link cue plumbing. The visual
    // dep-arrow layer reads `liveNodeIds` + `liveForestById` to classify a
    // dropped edge (archived vs collapsed) and the wired handlers turn the cue
    // into a one-click Restore / Expand. Always supplied here (the lead editor
    // can both restore and expand); the read-only temporal views + the member
    // view never see this code path.
    liveNodeIds,
    liveForestById,
    onRestoreArchivedEndpoint: (archivedNodeId: string) => {
      restoreNode.mutate({ nodeId: archivedNodeId });
    },
    onExpandToReveal: (collapsedAncestorId: string) => {
      expand(collapsedAncestorId);
    },
    // Round-4 ⑥ (#1043) — drag-to-pin + reset-to-auto-layout share this one
    // mutation: `(x, y)` numbers PIN, `(null, null)` RESETs. The backend is
    // idempotent on no-change so a flick-drag landing on the same coords
    // never logs spurious provenance.
    onSetPosition: (nodeId: string, x: number | null, y: number | null) => {
      setNodePosition.mutate({ nodeId, x, y });
    },
    // Round-4 ⑥ (#1043) — create a freeform `link_block` annotation. The
    // canvas's "+ link block" affordance fires this with the lead's title +
    // viewport-centre `(x, y)`; the new block round-trips through the live
    // tree refetch and lands at its pinned position.
    onCreateLinkBlock: (title: string, x: number, y: number) => {
      createLinkBlock.mutate({ title, x, y });
    },
    // The off-tree `link_block` nodes for THIS team. The canvas's layout
    // pass places them at their pinned `(layoutX, layoutY)` alongside the
    // tidy-tree.
    linkBlocks,
    // B2.12 — when the estimates layer is on, a card with a set estimate shows
    // its coarse `~Xd` meta (display-only). Off → no estimate meta, unchanged.
    estimatesEnabled,
    // Item 8 (#1043) — when the notes layer is on, a card with a recent note
    // shows a calm "🗒 <relative>" recency cue. Off → no cue, unchanged.
    notesEnabled,
    // #953 — persist a Blocked reason typed in the focused card's change-state
    // menu as a note, mirroring the drawer. Omitted when the notes layer is off
    // (the reason step then degrades to its calm placeholder, unchanged).
    ...(saveCardBlockedReason ? { onSaveBlockedReason: saveCardBlockedReason } : {}),
    ...(dependenciesEnabled
      ? {
          dependencies: dependenciesQuery.dependencies,
          onLink: linkDependency,
          // Click-to-link arming (#904) — the canvas reads the armed source +
          // toggles it through the shared store, so the banner + the card
          // emphasis stay in sync. Only present when the layer is on.
          linkArmId,
          onArmLink: setLinkArm,
          // Edge selection + popover (#929) — clicking a dependency arrow
          // selects it; the popover edits its note, inserts a node between, or
          // removes it. The node-title lookup names the endpoints factually.
          // Only present when the layer is on (the gate); off → no edge
          // interaction, byte-unchanged.
          selectedEdgeId,
          onSelectEdge: selectEdge,
          nodeTitleById,
          onSetEdgeNote: setEdgeNote,
          onInsertNodeBetween: insertNodeBetweenEdge,
          onRemoveEdge: removeEdge,
          edgeMutationPending,
        }
      : {}),
  };

  // The open node (drawer subject) + its parent, resolved from the one tree.
  const allNodes = flattenTree(roots);
  // A lightweight {id,title,state} list for the drawer's dependency picker +
  // row naming (only used when the gate is on).
  const dependencyNodeIndex = allNodes.map((n) => ({
    id: n.id,
    title: n.title,
    state: n.state,
  }));
  const openNodeData = openNodeId ? (allNodes.find((n) => n.id === openNodeId) ?? null) : null;
  const openNodeParent = openNodeData?.parentId
    ? (allNodes.find((n) => n.id === openNodeData.parentId) ?? null)
    : null;

  // #931 — the temporal tools (Rewind / Snapshots / Compare) group into ONE
  // "🕰 Time machine" menu. Each entry is present only when its own component
  // key is on (same gating as before); the menu button itself is omitted when
  // ALL three are off. Selecting an entry opens its existing overlay exactly as
  // before — the overlays are unchanged.
  const timeMachineEntries: TimeMachineEntry[] = [
    ...(rewindEnabled
      ? [
          {
            key: "rewind",
            icon: "⟲",
            label: "Rewind",
            description: "Scrub the map back to how it looked on a past day.",
            onSelect: () => setRewindOpen(true),
          },
        ]
      : []),
    ...(snapshotsEnabled
      ? [
          {
            key: "snapshots",
            icon: "◷",
            label: "Snapshots",
            description: "Save and revisit named points in the map's history.",
            onSelect: () => setTimeMachine("snapshots"),
          },
        ]
      : []),
    ...(compareEnabled
      ? [
          {
            key: "compare",
            icon: "⇆",
            label: "Compare",
            description: "See what changed between two points in time.",
            onSelect: () => setTimeMachine("compare"),
          },
        ]
      : []),
  ];

  // The unified attention bell + the node detail drawer are shared by BOTH the
  // desktop editor body and the M1 mobile outline-first layout (#1043, Wave 6).
  // Extracting them as elements keeps one source of truth so the mobile branch
  // never re-declares the (large) wiring; the drawer's `presentation` already
  // forks the shell (sheet on a phone, right-side drawer on desktop).
  const bellEl =
    bellOpen && bellMounted ? (
      <>
        <button
          type="button"
          data-testid="reminders-bell-scrim"
          aria-label="Close notifications"
          onClick={() => setBellOpen(false)}
          className="absolute inset-0 z-40 cursor-default"
          style={{ backgroundColor: PALETTE.canvas, opacity: 0.45 }}
        />
        <div className="absolute right-4 top-14 z-50 max-h-[calc(100%-5rem)]">
          <RemindersBell
            notificationsEnabled={notificationsEnabled}
            notifications={notificationFeed.items}
            notificationsLoading={notificationFeed.isLoading}
            notificationsError={notificationFeed.isError}
            onOpenNotification={openNotification}
            onMarkAllRead={() => markAllRead.mutate()}
            remindersEnabled={remindersEnabled}
            reminders={teamReminders.reminders}
            isLoading={teamReminders.isLoading}
            isError={teamReminders.isError}
            nodeTitleById={nodeTitleById}
            currentMembershipId={me.membershipId}
            teamTimezone={teamQuery.data?.timezone}
            onOpenNode={(id) => {
              openNode(id);
              setBellOpen(false);
            }}
            onClose={() => setBellOpen(false)}
            {...(notificationPrefsEnabled
              ? {
                  onOpenNotificationSettings: () => {
                    setBellOpen(false);
                    setNotificationPrefsOpen(true);
                  },
                }
              : {})}
          />
        </div>
      </>
    ) : null;

  // B4.4 (#1043) — the per-event x channel notification matrix, opened from the
  // bell footer's "notification settings" link (only when the layer is on). A
  // calm centred overlay reusing the bell's scrim idiom; each person edits their
  // OWN matrix (§A9). Mounted only when the layer is on AND the user opened it.
  const notificationPrefsEl =
    notificationPrefsEnabled && notificationPrefsOpen ? (
      <div className="absolute inset-0 z-50" data-testid="notification-prefs-overlay">
        <button
          type="button"
          data-testid="notification-prefs-scrim"
          aria-label="Close notification settings"
          onClick={() => setNotificationPrefsOpen(false)}
          className="absolute inset-0 cursor-default"
          style={{ backgroundColor: PALETTE.canvas, opacity: 0.45 }}
        />
        <div className="absolute left-1/2 top-12 z-50 max-h-[calc(100%-5rem)] w-[680px] max-w-[92vw] -translate-x-1/2 overflow-auto">
          <div
            className="rounded-2xl p-6"
            style={{
              backgroundColor: editorial ? theme.palette.canvas : "#FFFFFF",
              border: `1px solid ${editorial ? theme.palette.rule : "#E2E8E5"}`,
              boxShadow: "0 16px 48px rgba(26,22,18,0.18)",
            }}
          >
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                data-testid="notification-prefs-close"
                aria-label="Close notification settings"
                onClick={() => setNotificationPrefsOpen(false)}
                className="rounded p-1 text-base leading-none transition-opacity hover:opacity-70"
                style={{ color: theme.palette.muted }}
              >
                ✕
              </button>
            </div>
            <NotificationPrefsMatrix teamId={teamId} />
          </div>
        </div>
      </div>
    ) : null;

  const drawerEl = openNodeData ? (
    <NodeDetailDrawer
      teamId={teamId}
      node={openNodeData}
      parent={openNodeParent}
      members={members}
      nodeTitleById={nodeTitleById}
      {...(dependenciesEnabled
        ? {
            dependencies: dependenciesQuery.dependencies,
            allNodes: dependencyNodeIndex,
            onAddDependency: linkDependency,
            onRemoveDependency: unlinkDependency,
          }
        : {})}
      notesEnabled={notesEnabled}
      remindersEnabled={remindersEnabled}
      estimatesEnabled={estimatesEnabled}
      currentMembershipId={me.membershipId}
      isLead={me.isLead}
      // §A5 made apparent (#1043 item 4) — the lead editor only mounts for a
      // lead/co_lead (MapEditor role-branches up front), so the footer states
      // "you lead this team" as the reason they can edit.
      authorReason="lead"
      initialTab={openTab}
      focusNoteId={focusNoteId}
      onFocusConsumed={clearFocusNote}
      onClose={() => openNode(null)}
      onRename={(title) => rename(openNodeData.id, title)}
      onSetDescription={(description) => setDescription(openNodeData.id, description)}
      onSetState={(state) => setState(openNodeData.id, state)}
      onSetTargetDate={(date) => setTargetDate(openNodeData.id, date)}
      onSetEstimate={(minutes) => setEstimate(openNodeData.id, minutes)}
      onSetTiming={(patch) => patchNode.mutate({ nodeId: openNodeData.id, patch })}
      onAddSubtask={() => addSubtask(openNodeData.id)}
      onAssignToggle={(mid, assign) => assignToggle(openNodeData.id, mid, assign)}
      onOpenNode={openNode}
      onArchive={(cascade) => archive(openNodeData.id, cascade)}
      // M3 (#1043, Wave 6) — sheet on a phone in editorial; right-side drawer on
      // desktop / legacy (byte-unchanged).
      presentation={mobileEditorial ? "sheet" : "drawer"}
    />
  ) : null;

  // B2.13 (#1043, Wave 5) — the ⌘K / Ctrl-K find palette. A CORE Map capability
  // (no separate per-component toggle, founder 2026-05-26) that ships as part of
  // the editorial Map, so it mounts ONLY in editorial mode: with the
  // `task_editorial_ui` flag OFF (every team today) it never mounts, so no ⌘K
  // listener attaches and prod keyboard behaviour is byte-unchanged until the
  // flip. ↵ jumps + focuses a node (shared `select`); ⌘↵ opens its drawer
  // (shared `openNode`) — the palette reuses the one shared UI state, never its
  // own. Shared element so both the desktop body and the M1 mobile layout mount
  // a single instance over their (relative) container.
  const mapSearchEl = editorial ? (
    <MapSearch roots={roots} members={members} onJump={select} onOpenDrawer={openNode} />
  ) : null;

  // M1 (#1043, Wave 6) — the editorial Map's phone layout: an outline-first
  // surface with a tab strip + bottom action bar. Rendered ONLY when on a phone
  // AND editorial AND the lead hasn't dropped to the canvas. The shared bell +
  // drawer (as a sheet) ride along. Desktop / legacy / mobile-flag-OFF all fall
  // through to the unchanged editor body below.
  if (mobileEditorial && !mobileShowCanvas) {
    return (
      <TeamZoneContext.Provider value={teamQuery.data?.timezone || undefined}>
        <div data-testid="map-editor" className="relative">
          <MobileMapView
            teamId={teamId}
            outline={outlineProps}
            onShowCanvas={() => setMobileShowCanvas(true)}
            onOpenRewind={rewindEnabled ? () => setRewindOpen(true) : undefined}
            onAddTask={addFromToolbar}
          />
          {bellEl}
          {notificationPrefsEl}
          {drawerEl}
          {mapSearchEl}
        </div>
      </TeamZoneContext.Provider>
    );
  }

  return (
    <TeamZoneContext.Provider value={teamQuery.data?.timezone || undefined}>
      <div data-testid="map-editor" className="relative flex h-[calc(100vh-8rem)] flex-col">
        {/* Taskmap-window header row (#1043): [outline-collapse] … [🔔] [🕰 Time
          machine] [⚙] [+ Add]. The action cluster sits to the right; the
          outline collapse control leads. EDITORIAL gets a comfortable,
          well-spaced row (larger hit targets, more breathing room) below the
          Home · Taskmap secondary nav; LEGACY stays the cramped byte-identical
          toolbar. */}
        <div
          className={
            editorial
              ? "flex items-center justify-between gap-3 border-b px-5 py-3"
              : "flex items-center justify-between gap-2 border-b px-4 py-2"
          }
          style={{
            borderColor: editorial ? theme.palette.rule : PALETTE.softBorder,
            backgroundColor: editorial ? theme.palette.cardBg : PALETTE.canvas,
          }}
        >
          {/* The leading view control forks on the theme (#1043, IA shell, founder
            2026-05-26):
              - LEGACY: the corner Tree·Outline·Split segmented control,
                EXACTLY as today (byte-identical) — it owns all three switches.
              - EDITORIAL: ONE Taskmap window with the outline shown by default +
                collapsible. The toggle is a COLLAPSE control — "Hide outline"
                (⟨) when shown (the split), "Show outline" (⟩) when hidden (the
                canvas-only tree). No corner toggle, no Outline tab. */}
          {editorial ? (
            viewMode === "split" ? (
              <button
                type="button"
                data-testid="tree-split-toggle"
                aria-pressed={true}
                onClick={() => setViewMode("tree")}
                className="rounded-md border px-4 py-2 text-sm font-medium transition-colors"
                style={{
                  borderColor: theme.palette.rule,
                  color: theme.palette.ink,
                  fontFamily: theme.fonts.sans,
                }}
              >
                ⟨ Hide outline
              </button>
            ) : (
              <button
                type="button"
                data-testid="tree-split-toggle"
                aria-pressed={false}
                onClick={() => setViewMode("split")}
                className="rounded-md border px-4 py-2 text-sm font-medium transition-colors"
                style={{
                  borderColor: theme.palette.rule,
                  color: theme.palette.ink,
                  fontFamily: theme.fonts.sans,
                }}
              >
                ⟩ Show outline
              </button>
            )
          ) : (
            <ViewModeToggle mode={viewMode} onChange={setViewMode} />
          )}
          <div className={editorial ? "flex items-center gap-3" : "flex items-center gap-2"}>
            {/* The unified 🔔 attention bell (#768 reminders + #907 notifications).
              ONE entry, mounted when EITHER layer is on. An unread badge shows
              the notification unread count; the live editor is untouched when
              both gates are off. */}
            {bellMounted && (
              <button
                type="button"
                data-testid="reminders-bell-entry"
                onClick={() => setBellOpen(true)}
                className={
                  editorial
                    ? "relative rounded-md border px-4 py-2 text-sm font-medium transition-colors"
                    : "relative rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-slate-50"
                }
                style={{
                  borderColor: editorial ? theme.palette.rule : PALETTE.softBorder,
                  color: editorial ? theme.palette.ink : PALETTE.slate,
                  ...(editorial ? { fontFamily: theme.fonts.sans } : {}),
                }}
              >
                🔔 Notifications
                {notificationsEnabled && badgeLabel(notificationBadge.unreadCount) && (
                  <span
                    data-testid="notifications-badge"
                    className="-top-1.5 -right-1.5 absolute inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
                    style={{ backgroundColor: PALETTE.teal }}
                  >
                    {badgeLabel(notificationBadge.unreadCount)}
                  </span>
                )}
              </button>
            )}
            {/* The temporal tools (Rewind · Snapshots · Compare) grouped into ONE
              "🕰 Time machine" menu (#931). Each entry is gated by its own
              component key; the whole button is omitted when all three are off,
              so an ungated team sees no time-machine affordance. Selecting an
              entry opens its existing overlay unchanged. */}
            {timeMachineEntries.length > 0 && <TimeMachineMenu entries={timeMachineEntries} />}
            {/* #953 — a quiet in-map Settings entry for the lead, so Settings is
              reachable without the top nav. It routes to the EXISTING Map
              Settings page (`/teams/:id/map/settings`) — not a duplicate. This
              chrome is the lead editor only (`<LeadMapEditor/>`); members never
              see it (they don't manage settings). Unobtrusive: a small gear
              link, lower-key than the primary + Add. */}
            {teamId && (
              <Link
                to={`/teams/${teamId}/map/settings`}
                data-testid="map-settings-entry"
                aria-label="Map settings"
                title="Map settings"
                className={
                  editorial
                    ? "rounded-md border px-3 py-2 text-sm transition-colors"
                    : "rounded-md border px-2.5 py-1.5 text-xs transition-colors hover:bg-slate-50"
                }
                style={{
                  borderColor: editorial ? theme.palette.rule : PALETTE.softBorder,
                  color: editorial ? theme.palette.muted : PALETTE.muted,
                }}
              >
                ⚙
              </Link>
            )}
            {/* "+ outcome" (#1043, B2.14) — EDITORIAL-only chrome that ALWAYS
              creates a new root (a new outcome), distinct from "+ Add" (a
              sub-task under the focused node). Multi-outcome is a CORE Map
              capability (like ⌘K search), so it rides the `task_editorial_ui`
              gate with no separate per-component toggle. A quiet count chip
              ("N outcomes") sits beside it (matching the wireframe) once the
              map holds 2+ outcomes. */}
            {editorial && (
              <>
                {roots.length >= 2 && (
                  <span
                    data-testid="outcomes-count"
                    className="rounded-md px-2.5 py-2 text-sm"
                    style={{ color: theme.palette.muted, fontFamily: theme.fonts.sans }}
                  >
                    {roots.length} outcomes
                  </span>
                )}
                <button
                  type="button"
                  data-testid="toolbar-add-outcome"
                  onClick={createOutcome}
                  className="rounded-md border px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    borderColor: theme.palette.rule,
                    color: theme.palette.ink,
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  + outcome
                </button>
              </>
            )}
            {/* #2655 - paste a run sheet: one duty per line becomes a timed task. */}
            <button
              type="button"
              data-testid="toolbar-run-sheet"
              onClick={() => setRunSheetOpen(true)}
              className="rounded-md border px-3 py-1.5 text-sm font-medium transition-colors"
              style={{
                borderColor: theme.palette.rule,
                color: theme.palette.ink,
                fontFamily: theme.fonts.sans,
              }}
            >
              Paste a run sheet
            </button>
            {/* The focal + Add action stays a top-level toolbar item, last in the
              cluster so it reads as the clear primary action. */}
            <button
              type="button"
              data-testid="toolbar-add"
              onClick={addFromToolbar}
              className={
                editorial
                  ? "rounded-md px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  : "rounded-md px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
              }
              style={{ backgroundColor: PALETTE.teal }}
            >
              + Add
            </button>
          </div>
        </div>

        {/* B8.4 (#1043) — the top "PLAN COMPLETED" banner, shown when the WHOLE
          board is complete (every outcome's leaves done, ≥1 task). Calm, factual,
          done-GREEN; the `snapshot & close` ghost action appears only when the
          snapshots layer is on (it pins a closing snapshot, never archives). It
          sits just below the header toolbar — the topmost banner, like the
          wireframe. EDITORIAL-only (gated by `boardComplete`). */}
        {boardComplete && (
          <CompletedPlanBanner
            leafCount={leafCount}
            onSnapshotClose={snapshotsEnabled ? pinClosingSnapshot : undefined}
            snapshotPending={createSnapshot.isPending}
          />
        )}

        {/* A calm reparent notice (only on a rejected move). */}
        {reparentNotice && (
          <div
            data-testid="reparent-notice"
            className="border-b px-4 py-1.5 text-[11px]"
            style={{
              borderColor: PALETTE.softBorder,
              backgroundColor: "#FEF3C7",
              color: "#B45309",
            }}
          >
            {reparentNotice}
          </div>
        )}

        {/* A calm dependency-link notice (rejected cycle / not-author). Same
          factual, non-alarm treatment as the reparent one (§A9). */}
        {dependencyNotice && (
          <div
            data-testid="dependency-notice"
            className="border-b px-4 py-1.5 text-[11px]"
            style={{
              borderColor: PALETTE.softBorder,
              backgroundColor: "#FEF3C7",
              color: "#B45309",
            }}
          >
            {dependencyNotice}
          </div>
        )}

        {/* "What do I do next?" coach (#980) — a calm, progressive strip that
          reads the LIVE tree and shows the lead's single next move (break it
          down → assign → move state). It advances as the lead acts and
          self-hides once the loop is rolling; dismiss persists per-team. It is
          ABOVE the canvas (chrome), so it never sets node x/y (§A7). */}
        <NextStepCoach roots={roots} teamId={teamId} />

        {/* B3.6 "what changed since you last looked" (#765) — a gentle, gated,
          dismissable orientation panel showing ONLY the lead's own slice. It
          self-suppresses when the gate is off / the slice is empty / dismissed. */}
        <MapChangeDigest
          teamId={teamId}
          membershipId={me.membershipId}
          teamTimezone={teamQuery.data?.timezone}
        />

        {/* The active editor surface — the canvas/outline alone (the docked
          History rail was removed round-3; activity lives on the Home
          dashboard). The surface fills the full width of the Taskmap window. */}
        <div className="flex min-h-0 flex-1" data-testid="map-surface-row">
          {viewMode === "split" ? (
            <SplitView outline={outlineProps} canvas={canvasProps} />
          ) : viewMode === "outline" ? (
            <div className="min-h-0 flex-1" data-testid="outline-surface">
              <OutlineEditor {...outlineProps} />
            </div>
          ) : editorial ? (
            // EDITORIAL Taskmap, outline HIDDEN (#1043, IA shell, founder
            // 2026-05-26): the canvas ALONE — NO docked outline rail. This is the
            // collapsed state of the one Taskmap window; the DEFAULT is the split
            // (outline shown, `viewMode === "split"` above). The header's "Show
            // outline" (⟩) / "Hide outline" (⟨) control flips between them.
            <div className="flex min-h-0 flex-1" data-testid="tree-surface">
              <div className="relative min-w-0 flex-1">
                <TaskCanvas {...canvasProps} />
              </div>
            </div>
          ) : (
            // LEGACY tree home (B2.1), byte-identical: the canvas with the docked,
            // select-only OUTLINE rail beside it. Selecting a row focuses the node
            // on the canvas (shared `selectedId`); full keyboard editing lives in
            // the dedicated Outline / Split modes (the corner toggle).
            <div className="flex min-h-0 flex-1" data-testid="tree-surface">
              <TaskOutlineRail
                roots={roots}
                selectedId={selectedId}
                onSelect={select}
                onAddSubtask={addFromToolbar}
              />
              <div className="relative min-w-0 flex-1">
                <TaskCanvas {...canvasProps} />
              </div>
            </div>
          )}
        </div>

        {/* Visibility line (§A8 / trustable pillar) — factual, not a setting. */}
        <div
          className="border-t px-4 py-1.5 text-[11px]"
          style={{
            borderColor: PALETTE.softBorder,
            backgroundColor: PALETTE.canvas,
            color: PALETTE.faint,
          }}
          data-testid="visibility-line"
        >
          Everyone on this team can see the whole map. Your parts are highlighted.
        </div>

        {/* The unified attention bell (#768 + #907) floats over the live, dimmed
          editor. Notifications (the lead's own slice) + the team reminder feed
          in one panel. A notification "open →" deep-links to its note; a
          reminder "open →" opens that node's drawer; both close the bell. Only
          mounted when a layer is on AND it's open. (Shared element — see
          `bellEl`; the M1 mobile layout reuses the same instance.) */}
        {bellEl}
        {notificationPrefsEl}

        {/* B2.5 — the node detail drawer floats over the live, dimmed editor.
          It reads the live `TreeNode` (the one `useTaskNodes` source) and
          fires the same mutations the canvas/outline use. (Shared element — see
          `drawerEl`; on a phone in editorial it presents as a bottom-sheet via
          the drawer's `presentation` prop, and the M1 mobile layout reuses the
          same instance.) */}
        {drawerEl}

        {/* B2.13 — the ⌘K find palette floats over the relative editor body. Core
          capability, mounted only in editorial mode (see `mapSearchEl`). */}
        {mapSearchEl}

        <RunSheetPanel
          open={runSheetOpen}
          onOpenChange={setRunSheetOpen}
          teamId={teamId}
          members={members}
        />
      </div>
    </TeamZoneContext.Provider>
  );
}
